import { useCallback, useEffect, useState } from 'react';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { formsAPI, tasksAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { useProject } from '../../layout/ProjectLayout';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';

const FIELD_TYPES = ['text', 'textarea', 'email', 'select', 'date', 'number'];

function FormEditor({ form, groups, projectId, onClose, onSaved }) {
  const [title, setTitle] = useState(form?.title || '');
  const [description, setDescription] = useState(form?.description || '');
  const [groupId, setGroupId] = useState(form?.target_group_id || groups[0]?.id || '');
  const [fields, setFields] = useState(form?.fields || [{ label: 'Your request', type: 'textarea', required: true, options: [] }]);
  const [active, setActive] = useState(form?.active ?? true);

  const setField = (i, patch) => setFields((f) => f.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const submit = async (e) => {
    e.preventDefault();
    const data = { title, description, target_group_id: groupId, fields, active };
    if (form) await formsAPI.update(form.id, data);
    else await formsAPI.create(projectId, data);
    onSaved();
    onClose();
  };

  return (
    <Modal title={form ? 'Edit form' : 'New public form'} onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Form title">
            <input value={title} onChange={(e) => setTitle(e.target.value)} required className={inputCls} />
          </Field>
          <Field label="Submissions become tasks in">
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputCls}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Description (shown to visitors)">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inputCls} />
        </Field>
        <div>
          <span className="mb-1.5 block text-sm text-pm-muted">Fields</span>
          <ul className="space-y-2">
            {fields.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <input
                  value={f.label}
                  onChange={(e) => setField(i, { label: e.target.value })}
                  placeholder="Label"
                  className={`${inputCls} py-1.5 text-xs`}
                />
                <select value={f.type} onChange={(e) => setField(i, { type: e.target.value })} className={`${inputCls} w-24 py-1.5 text-xs`}>
                  {FIELD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                {f.type === 'select' && (
                  <input
                    value={(f.options || []).join(', ')}
                    onChange={(e) => setField(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                    placeholder="a, b, c"
                    className={`${inputCls} w-28 py-1.5 text-xs`}
                  />
                )}
                <label className="flex items-center gap-1 text-xs text-pm-muted">
                  <input type="checkbox" checked={Boolean(f.required)} onChange={(e) => setField(i, { required: e.target.checked })} className="accent-[var(--pm-accent)]" />
                  req
                </label>
                <button type="button" onClick={() => setFields((x) => x.filter((_, j) => j !== i))} className="rounded p-1 text-pm-faint hover:text-pm-danger">
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => setFields((f) => [...f, { label: '', type: 'text', required: false, options: [] }])}
            className="mt-2 inline-flex items-center gap-1 text-xs text-pm-muted hover:text-pm-text"
          >
            <Plus size={12} /> Add field
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm text-pm-muted">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[var(--pm-accent)]" />
          Form is active (public URL accepts submissions)
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="submit" disabled={!title.trim() || fields.length === 0} className={btnPrimary}>
            {form ? 'Save form' : 'Create form'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function FormsScreen() {
  const { project } = useProject();
  const [forms, setForms] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | form
  const user = getUser();
  const readOnly = user?.role === 'guest';

  const load = useCallback(async () => {
    const [f, g] = await Promise.all([formsAPI.list(project.id), tasksAPI.groups(project.id)]);
    setForms(f.data.forms);
    setGroups(g.data.groups);
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  const copyUrl = async (f) => {
    const url = `${window.location.origin}/f/${f.public_slug}`;
    await navigator.clipboard?.writeText(url);
    window.alert(`Public form URL copied:\n${url}`);
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-pm-faint">Public forms create tasks in this project. Anti-bot guarded, no login needed.</p>
        {!readOnly && (
          <button type="button" onClick={() => setEditing('new')} className={`${btnPrimary} inline-flex items-center gap-1.5`}>
            <Plus size={14} />
            New form
          </button>
        )}
      </div>
      <ul className="divide-y divide-pm-border/60 rounded-lg border border-pm-border bg-pm-surface">
        {forms.map((f) => (
          <li key={f.id} className="flex items-center gap-3 px-3 py-3">
            <span className={`h-2 w-2 shrink-0 rounded-full ${f.active ? 'bg-pm-accent' : 'bg-pm-faint'}`} />
            <button type="button" onClick={() => !readOnly && setEditing(f)} className="min-w-0 flex-1 truncate text-left text-sm hover:text-pm-accent">
              {f.title}
            </button>
            <span className="text-xs text-pm-faint">{f.submissions_count} submission(s)</span>
            <button type="button" onClick={() => copyUrl(f)} className="rounded p-1.5 text-pm-faint hover:text-pm-text" title="Copy public URL">
              <Copy size={13} />
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Delete form "${f.title}"?`)) return;
                  await formsAPI.remove(f.id);
                  await load();
                }}
                className="rounded p-1.5 text-pm-faint hover:text-pm-danger"
              >
                <Trash2 size={13} />
              </button>
            )}
          </li>
        ))}
        {forms.length === 0 && <li className="px-3 py-10 text-center text-sm text-pm-muted">No forms yet.</li>}
      </ul>
      {editing && (
        <FormEditor
          form={editing === 'new' ? null : editing}
          groups={groups}
          projectId={project.id}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
